// The view-runtime BRIDGE (BUILD-DEC-097, reactivating BUILD-DEC-002): adapts the bespoke `useChat`
// transport controller onto the external-store runtime the adopted headless chat primitives consume.
// The library owns PRESENTATION ONLY (BUILD-DEC-002: transport/state stay with the agent module) —
// every message below comes from the controller's composed turns (the resolved transcript projection +
// the human's own preserved input + the guard-cleared streaming preview; P-SELF, G-160), a composer
// submit forwards ONLY the typed text to `chat.send` (no screen id, no context — N-CHAT-1 AC-4), and
// cancel routes to the human-only interrupt (N-CHAT-2). The bridge fabricates no turn.

import { useExternalStoreRuntime } from '@assistant-ui/react';
import type {
  AppendMessage,
  AssistantRuntime,
  ThreadAssistantMessagePart,
  ThreadMessageLike,
} from '@assistant-ui/react';
import { foldTraceSummaries } from '../surfaces/chat-reasoning.ts';
import type { ChatActivityItem } from '../surfaces/projections.ts';
import type { ChatTurn, ChatTurnPart } from '../surfaces/chat-turns.ts';
import type { ChatController } from '../surfaces/useChat.ts';

/** True for the single synthetic in-progress turn the controller composes while a preview assembles
 *  (its id is a render key the controller mints — see `composeTurns`; never an agent identifier). */
export function isStreamingTurn(turn: ChatTurn): boolean {
  return turn.id.startsWith('streaming-agent-');
}

/** Adapt one already-sanitised activity item into Assistant UI's display-only tool part. */
function activityPart(row: ChatActivityItem): ThreadAssistantMessagePart {
  const settled = row.outcome !== 'running';
  return {
    type: 'tool-call',
    toolCallId: row.toolCallId ?? row.id,
    // The closed operational kind is safe surface vocabulary; never expose a raw provider tool name.
    toolName: row.kind,
    args: {},
    argsText: '{}',
    artifact: row,
    ...(settled ? { result: { outcome: row.outcome } } : {}),
    ...(row.outcome === 'error' || row.outcome === 'gate_refused' ? { isError: true } : {}),
  };
}

/** Preserve the server's publication order exactly; this bridge never regroups parts by kind. */
function orderedPart(part: ChatTurnPart): ThreadAssistantMessagePart {
  if (part.type === 'activity') return activityPart(part.activity);
  if (part.type === 'reasoning') return { type: 'reasoning', text: part.text };
  return { type: 'text', text: part.text };
}

/** Carry a folded run of reasoning summaries as ONE display-only part. The view library renders each part
 *  in isolation — a component cannot see its siblings — so the fold has to be decided here, before
 *  conversion, and travel on the artifact. The part's name is the closed operational kind, never a
 *  provider's tool name; the summaries themselves ride verbatim for the disclosure to list. */
function summaryGroupPart(rows: readonly ChatActivityItem[]): ThreadAssistantMessagePart {
  return {
    type: 'tool-call',
    toolCallId: `reasoning-${rows[0]?.id ?? 'group'}`,
    toolName: 'thinking_summary',
    args: {},
    argsText: '{}',
    artifact: { kind: 'thinking_summary_group', rows },
    result: { outcome: 'ok' },
  };
}

/** One turn's parts, with each run of adjacent reasoning summaries folded into a single disclosure part.
 *  Anything else the run recorded — a tool call, an answer paragraph — ends the fold and keeps its place,
 *  so thinking reported after a memory read or after a paragraph is still reported after it. */
function assistantParts(parts: readonly ChatTurnPart[]): ThreadAssistantMessagePart[] {
  return foldTraceSummaries(parts, (part) =>
    part.type === 'activity' && part.activity.kind === 'thinking_summary' ? part.activity : null,
  ).map((entry) =>
    entry.type === 'summaries' ? summaryGroupPart(entry.rows) : orderedPart(entry.row),
  );
}

/** Map one composed turn onto the view runtime's structured message shape. Activity rows become stable,
 *  render-only tool parts; a run of adjacent reasoning summaries becomes ONE folded disclosure part; the live
 *  reasoning sentence stays its own part; answer text remains verbatim. The projection is already
 *  guard-cleared — the view adds no tool arguments, results, or reasoning. */
export function convertTurn(turn: ChatTurn): ThreadMessageLike {
  const metadata =
    turn.sourceRef === undefined && turn.legacyInboundMessage !== true
      ? undefined
      : {
          custom: {
            ...(turn.sourceRef === undefined ? {} : { sourceRef: turn.sourceRef }),
            ...(turn.legacyInboundMessage === true ? { legacyInboundMessage: true } : {}),
          },
        };
  if (turn.author === 'human') {
    return { role: 'user', id: turn.id, content: [{ type: 'text', text: turn.text }], metadata };
  }
  // A TEAMMATE's turn is neither the reader's own nor Ritona's answer, so it takes neither bubble: `user`
  // is the right-aligned "me" bubble (it would attribute a colleague's words to the reader) and
  // `assistant` is Ritona's (it would attribute them to the agent). The third role is the only seat with
  // its own renderer — see `TeammateMessage` in ChatConversation.tsx. Verbatim text, exactly one part,
  // which is all this role accepts.
  if (turn.author === 'teammate') {
    return { role: 'system', id: turn.id, content: [{ type: 'text', text: turn.text }], metadata };
  }
  // A legacy run predates the server's interleaved parts, so it keeps its activity-before-answer shape —
  // rebuilt as parts here so both vintages take the SAME fold rather than only the new one being tidied.
  const parts: readonly ChatTurnPart[] = turn.parts ?? [
    ...(turn.activity ?? []).map((activity): ChatTurnPart => ({ type: 'activity', activity })),
    ...(turn.text.length > 0 ? [{ type: 'text' as const, text: turn.text }] : []),
  ];
  return {
    role: 'assistant',
    id: turn.id,
    content: assistantParts(parts),
    metadata,
    status: isStreamingTurn(turn) ? { type: 'running' } : { type: 'complete', reason: 'stop' },
  };
}

/** Extract the human's typed text from a composer submission — text parts ONLY; nothing else exists on
 *  the thin transport (no attachment, no screen context — N-CHAT-1 AC-4). */
function appendedText(message: AppendMessage): string {
  const texts: string[] = [];
  for (const part of message.content) {
    if (part.type === 'text') texts.push(part.text);
  }
  return texts.join('\n\n');
}

/** Bridge every composed turn — including the active structured streaming turn — into the view runtime
 *  consumed by the conversation region. Tool parts are display-only: client invocation stays disabled. */
export function useRitonaChatRuntime(chat: ChatController): AssistantRuntime {
  return useExternalStoreRuntime<ChatTurn>({
    messages: chat.turns,
    convertMessage: convertTurn,
    // Module-driven: `running` comes from the transcript/stream state the owning module reports; the
    // view never sets it (N-CHAT-2 AC-1).
    isRunning: chat.runState === 'running',
    isSendDisabled: chat.sending,
    onNew: async (message: AppendMessage): Promise<void> => {
      await chat.send(appendedText(message));
    },
    onCancel: async (): Promise<void> => {
      await chat.interrupt();
    },
  });
}
